
import {ErrorResponse, ProductData, StudioResponse, SessionData} from "../types/StudioAPITypes";
import {fourOhFour, logout} from "../utils/routerUtils";

// Route creators
// TODO make API endpoint configurable
const api = `${window.location.protocol}//${window.location.hostname}/api`;
const sessionRoute = `${api}/session`;
const projectsRoute = `${api}/projects`;
const projectRoute = (project: string) => `${projectsRoute}/${project}`;
const productsRoute = (project: string) => `${projectRoute(project)}/products`;
const productRoute = (project: string, product: string) => `${productsRoute(project)}/${product}`;
const productStatusRoute = (project: string, product: string) => `${productRoute(project, product)}/status`;
const productConversionRoute = (project: string) => `${productsRoute(project)}/convert`;

type Credentials = 'include' | 'same-origin';

export interface fetchTrackPogressOpts {
  method: string,
  credentials: any,
  headers: {},
  body: any
}

export default class StudioAPI {

  private static instance: StudioAPI = new StudioAPI();

  static getInstance() {
    return this.instance;
  }

  private credentials: Credentials = 'include';

  constructor() {
    if (StudioAPI.instance) {
      return StudioAPI.instance;
    }

    // TODO Set credentials policy to 'same-origin' for production environment
    // TODO fetchWithProgress function sets useCredentials req header based on this variable -
    // This may be able to be removed under the same-origin policy when in prod
    this.credentials = 'include';
  }

  fetchProjects(): Promise<StudioResponse<{}>> {
    return this.getJSON(projectsRoute);
  }

  fetchProject(projectId: string): Promise<StudioResponse<{}>> {
    return this.getJSON(projectRoute(projectId));
  }

  postSession(data: SessionData): Promise<StudioResponse<{}>> {
    return this.sendJson(sessionRoute, 'post', data);
  }

  deleteSession(): Promise<StudioResponse<{}>> {
    return this.deleteEntity(sessionRoute);
  }

  fetchSession(): Promise<StudioResponse<{}>> {
    return this.getJSON(sessionRoute);
  }

  deleteProduct(projectId: string, productId: string): Promise<StudioResponse<{}>> {
    return this.deleteEntity(productRoute(projectId, productId));
  }

  updateProduct(projectId: string, productId: string, data: ProductData): Promise<StudioResponse<{}>> {
    return this.sendJson(productRoute(projectId, productId), 'put', data);
  }

  addProduct(projectId: string, data: ProductData): Promise<StudioResponse<{}>> {
    return this.sendJson(productsRoute(projectId), 'post', data);
  }

  duplicateProduct(projectId: string, data: any): Promise<StudioResponse<{}>> {
    return this.sendJson(productsRoute(projectId), 'post', data);
  }

  fetchProductStatus(projectId: string, productId: string): Promise<StudioResponse<{}>>  {
    return this.getJSON(productStatusRoute(projectId, productId));
  }

  importFile(projectId: string, file: File, onProgress?: (e: any, uploadStartTime: number) => void): Promise<StudioResponse<{}>> {
    return this.sendMultipartFile(productConversionRoute(projectId),
      this.convertFileToFormData(file),
      onProgress)
  }

  private convertFileToFormData(file: File) {
    let formData = new FormData();
    formData.append('sourceFile', file);
    return formData
  }

  private sendJson(route: string, method: 'post' | 'put', data: any): Promise<StudioResponse<{}>>  {
    return fetch(route, {
      method: method,
      credentials: this.credentials,
      headers: {"Content-Type": "application/json; charset=utf-8"},
      body: JSON.stringify(data)
    }).then(this.handleFetchResponse);
  }

  private fetchWithProgress(route: string, onProgress: any, opts: fetchTrackPogressOpts): any {
    return new Promise( (res, rej)=>{
      var uploadStartTime = new Date().getTime();
      var xhr = new XMLHttpRequest();
      xhr.open(opts.method, route);
      xhr.withCredentials = true;
      xhr.onload = (e: any) => res(e.target && e.target.responseText ? e.target.responseText: "");
      xhr.onerror = rej;
      if (xhr.upload && onProgress)
        xhr.upload.onprogress = (e: any) => {
        onProgress(e, uploadStartTime)
      };
      xhr.send(opts.body);
    });
  }

  private sendMultipartFile(route: string, formData: any, onProgress: any): Promise<StudioResponse<{}>>  {
    // Note: Not using fetch api here because fetch api does not support tracking file upload progress.

    return this.fetchWithProgress(route, onProgress ,{
      method: "post",
      credentials: this.credentials,
      headers: {},
      body: formData
    }).then(this.handleXHRResponse);
  }

  private getJSON(route: string): Promise<StudioResponse<{}>> {
    return fetch(route, {
      credentials: 'include'
    }).then(this.handleFetchResponse)
  }

  private deleteEntity(route: string): Promise<StudioResponse<{}>> {
    return fetch(route, {
      method: 'delete',
      credentials: 'include'
    }).then(this.handleFetchResponse)
  }

  private handleFetchResponse(response: Response): Promise<StudioResponse<{}>> {
    return response.json().then(json => {
      if (!response.ok) {
        const error: ErrorResponse = Object.assign({}, json, {
          status: response.status,
          statustext: response.statusText
        });

        if (error.code === 401) {
          logout();
        }

        if (error.code === 404) {
          fourOhFour();
        }

        return Promise.reject(error);
      }

      return json;
    });
  }

  private handleXHRResponse(response: string): Promise<StudioResponse<{}>> {
    var responseJSON = JSON.parse(response);
    if (responseJSON.code !== 200) {
      const error: ErrorResponse = Object.assign({}, responseJSON, {
        status: responseJSON.status,
        statustext: responseJSON.statusText
      });

      if (error.code === 401) {
        logout();
      }

      if (error.code === 404) {
        fourOhFour();
      }

      return Promise.reject(error);
    }

    return responseJSON;
  }
}
